import React, { useEffect, useState } from 'react';
import { CardAffinityGroup } from './five-step';
import { Button, Col, Modal, Row, Spin, Timeline } from 'antd';
import usePostRequestUpgrade from '../../../hooks/utils/usePostRequestUpgrade';
import { CheckOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { validateStatus } from '../../../utils/utils';
import usePutRequestUpgrade from '../../../hooks/utils/usePutRequestUpgrade';
import config from 'config';
import usePatchRequest from '../../../hooks/utils/usePatchRequest';
import { useNavigate, useParams } from 'react-router-dom';
const url: string = config.api.url;
import './modal-creation-steps.css';

const ModalCreationSteps = ({
  open,
  cardAffinityGroup,
  onCancel,
} : {
  open: boolean;
  cardAffinityGroup: CardAffinityGroup | undefined;
  onCancel: () => void;
}) => {
  const navigate = useNavigate();
  const { issuer } = useParams<{ issuer: string }>();
  const { executePost: executeMainData, postLoading: loadingMainData, postError: errorMainData  } = usePostRequestUpgrade({ path: 'v1/card_affinity_groups' });
  const { executePost: executeFeesData, postLoading: loadingFeesData, postError: errorFeesData  } = usePostRequestUpgrade({ path: '' });
  const { executePut: executeChargesData, putLoading: loadingChargesData, putError: errorChargesData  } = usePutRequestUpgrade({ path: '' });
  const { executePut: executeLimitsData, putLoading: loadingLimitsData, putError: errorLimitsData} = usePutRequestUpgrade({ path: '' });
  const { executePatch: executeUsageLimitsData, patchLoading: loadingUsageLimitsData, patchError: errorUsageLimitsData} = usePatchRequest({ path: '' });
  const [cardAffinityGroupId, setCardAffinityGroupId] = useState(null);
  const [feesSuccess, setFeesSuccess] = useState<boolean>(null);
  const [chargesSuccess, setChargesSuccess] = useState<boolean>(null);
  const [limitsSuccess, setLimitsSuccess] = useState<boolean>(null);
  const [usageLimitsSuccess, setUsageLimitsSuccess] = useState<boolean>(null);

  useEffect(() => {
    const executionMainData = async () => {
      try {
        const response = await executeMainData({ data: cardAffinityGroup?.affinity_group });
        if(validateStatus(response?.status)) {
          setCardAffinityGroupId(response?.data?.id);
        }
      } catch (e: unknown) {
        console.log("Error en la request principal");
        setCardAffinityGroupId(null);
      }
    }

    if(cardAffinityGroup !== undefined && cardAffinityGroup !== null) {
      executionMainData();
    }
  }, [cardAffinityGroup]);

  useEffect(() => {
    const executionFeesData = async () => {
      if(cardAffinityGroup?.fees !== null && cardAffinityGroup?.fees?.length > 0) {
        try {
          const feesSuccessFinal: boolean = true;
          await cardAffinityGroup?.fees?.forEach( async (fee) => {
            const response = await executeFeesData({ url: url + `/v1/card_affinity_groups/${cardAffinityGroupId}/fee`, data: fee });
            if(!validateStatus(response?.status))
              setFeesSuccess(false);
          })
          setFeesSuccess(feesSuccessFinal);
        } catch (e: unknown) {
          console.log("Error en la request de fees");
          setFeesSuccess(false);
        }
      } else {
        setFeesSuccess(true);
      }
    }

    if(cardAffinityGroupId !== null) {
      executionFeesData();
    }
  }, [cardAffinityGroupId]);

  useEffect(() => {
    const executionChargesData = async () => {
      if(cardAffinityGroup?.charges !== null && cardAffinityGroup?.charges?.charges?.length > 0) {
        try {
          const response = await executeChargesData({ url: url + `/v1/card_affinity_groups/${cardAffinityGroupId}/charges`, data: cardAffinityGroup?.charges });
          if(validateStatus(response?.status)) {
            setChargesSuccess(true);
          }
        } catch (e: unknown) {
          console.log("Error en la request de charges");
          setChargesSuccess(false);
        }
      } else {
        setChargesSuccess(true);
      }
    }

    if(feesSuccess !== null) {
      executionChargesData();
    }
  }, [feesSuccess]);

  useEffect(() => {
    const executionLimitsData = async () => {
      if(cardAffinityGroup?.transaction_limits) {
        try {
          const response = await executeLimitsData({ url: url + `/v1/card_affinity_groups/${cardAffinityGroupId}/transactions/limits`, data: cardAffinityGroup?.transaction_limits });
          if(validateStatus(response?.status)) {
            setLimitsSuccess(true);
          }
        } catch (e: unknown) {
          console.log("Error en la request de limits");
          setLimitsSuccess(false);
        }
      } else {
        setLimitsSuccess(true);
      }
    }

    if(chargesSuccess !== null) {
      executionLimitsData();
    }
  }, [chargesSuccess]);

  useEffect(() => {
    const executionUsageLimits = async () => {
      if(cardAffinityGroup?.usage_limits) {
        try {
          const response = await executeUsageLimitsData({ url: url + `/v1/card_affinity_groups/${cardAffinityGroupId}/usage_limits`, data: cardAffinityGroup?.usage_limits });
          if(validateStatus(response?.status)) {
            setUsageLimitsSuccess(true);
          }
        } catch (e: unknown) {
          console.log("Error en la request de usage limits");
          setUsageLimitsSuccess(false);
        }
      } else {
        setUsageLimitsSuccess(true);
      }
    }

    if(limitsSuccess !== null) {
      executionUsageLimits();
    }
  }, [limitsSuccess]);

  return (
    <Modal
      open={open}
      title={"Creando grupo de afinidad"}
      onCancel={() => {
        onCancel();
      }}
      footer={false}
    >
      <Row gutter={24}>
        <Col span={18} offset={6} style={{ marginTop: '40px' }}>
          <Timeline
            items={[
              {
                dot:  cardAffinityGroupId !== null ? <CheckOutlined className={"icon-success"} /> : errorMainData !== null ? <CloseCircleOutlined className={"icon-error"} /> : <Spin tip="Loading" size="small" />,
                color: '#a368fc',
                children: (cardAffinityGroupId !== null && errorMainData !== null) ? (
                  <>
                    <span className={"title-icon"}>Creando datos principales</span>
                    <Button size="middle" type="link" onClick={() => navigate(`/${issuer}/card-affinity-groups/${cardAffinityGroupId}#main-data`) }>
                      Ver
                    </Button>
                  </>
                ): <span className={"title-icon"}>Creando datos principales</span>
              },
              {
                dot: errorMainData !== null ? <CloseCircleOutlined className={"icon-error"} /> : (cardAffinityGroupId !== null && feesSuccess !== null ? (feesSuccess ? <CheckOutlined className={"icon-success"} /> : <CloseCircleOutlined className={"icon-error"} />) : <Spin tip="Loading" size="small" />),
                color: '#a368fc',
                children: (cardAffinityGroupId !== null && !feesSuccess) ? (
                  <>
                    <span className={"title-icon"}>Creando impuestos</span>
                    <Button size="middle" type="link"
                            onClick={() => navigate(`/${issuer}/card-affinity-groups/${cardAffinityGroupId}#fees`)}>
                      Ver
                    </Button>
                  </>
                ) : <span className={"title-icon"}>Creando impuestos</span>
              },
              {
                dot: errorMainData !== null ? <CloseCircleOutlined className={"icon-error"} /> : (feesSuccess !== null && chargesSuccess !== null ? ( chargesSuccess ? <CheckOutlined className={"icon-success"} /> : <CloseCircleOutlined className={"icon-error"} />) : <Spin tip="Loading" size="small" />),
                color: '#a368fc',
                children: (cardAffinityGroupId !== null && !chargesSuccess) ? (
                  <>
                    <span className={"title-icon"}>Creando cargos</span>
                    <Button size="middle" type="link" onClick={() => navigate(`/${issuer}/card-affinity-groups/${cardAffinityGroupId}#charges`) }>
                      Ver
                    </Button>
                  </>
                ): <span className={"title-icon"}>Creando cargos</span>
              },
              {
                dot: errorMainData !== null ? <CloseCircleOutlined className={"icon-error"} /> : (chargesSuccess !== null && limitsSuccess !== null ? ( limitsSuccess ? <CheckOutlined className={"icon-success"} /> : <CloseCircleOutlined className={"icon-error"} />) : <Spin tip="Loading" size="small" />),
                color: '#a368fc',
                children: (cardAffinityGroupId !== null && !limitsSuccess) ? (
                    <>
                      <span className={"title-icon"}>Creando limites</span>
                      <Button size="middle" type="link" onClick={() => navigate(`/${issuer}/card-affinity-groups/${cardAffinityGroupId}#limits`) }>
                        Ver
                      </Button>
                    </>
                  ): <span className={"title-icon"}>Creando limite</span>
              },
              {
                dot: errorMainData !== null ? <CloseCircleOutlined
                  className={"icon-error"} /> : (limitsSuccess !== null && usageLimitsSuccess !== null ? (usageLimitsSuccess ?
                    <CheckOutlined className={"icon-success"} /> : <CloseCircleOutlined className={"icon-error"} />) :
                  <Spin tip="Loading" size="small" />),
                color: '#a368fc',
                children: (cardAffinityGroupId !== null && !usageLimitsSuccess) ? (
                  <>
                    <span className={"title-icon"}>Creando limitadores de uso</span>
                    <Button size="middle" type="link"
                            onClick={() => navigate(`/${issuer}/card-affinity-groups/${cardAffinityGroupId}#usage-limits`)}>
                      Ver
                    </Button>
                  </>
                ) : <span className={"title-icon"}>Creando limitadores de uso</span>,
              },
            ]}
          />
        </Col>
        <Col span={24} style={{ textAlign: 'right' }}>
          {
            cardAffinityGroupId !== null ? (
              <>
                <Button size="middle" onClick={onCancel}>
                  Cancelar
                </Button>
                <Button size="middle" type="primary" onClick={ () => navigate(`/${issuer}/card-affinity-groups/${cardAffinityGroupId}`) }>
                  Ver
                </Button>
              </>
            ) :
              <>
                <Button size="middle" onClick={onCancel}>
                  Cancelar
                </Button>
              </>
          }
        </Col>
      </Row>
    </Modal>
  )
};

export default ModalCreationSteps;